import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { API_URL } from "@/utils/api";

export default function ProtectedRoute({ children, requiredRoles = [] }) {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState("Loading...");
  const navigate = useNavigate();
  const location = useLocation();
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const checkAuth = useCallback(async () => {
    // Check for JWT token in localStorage first, then sessionStorage as fallback
    let token = localStorage.getItem("auth_token");
    
    // Fallback to sessionStorage for mobile Safari issues
    if (!token) {
      token = sessionStorage.getItem("auth_token_backup");
      if (token) {
        // Restore to localStorage
        try {
          localStorage.setItem("auth_token", token);
        } catch (e) {
          console.warn("Could not restore token to localStorage");
        }
      }
    }
    
    // No token means not logged in
    if (!token) {
      console.log("No auth token found, redirecting to login");
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
      return;
    }
    
    try {
      // Add timeout to handle cold starts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        // Token might be invalid/expired - clear it
        if (response.status === 401) {
          console.log("Token invalid/expired, clearing and redirecting");
          localStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_token_backup");
        }
        throw new Error("Not authenticated");
      }
      
      const userData = await response.json();
      setUser(userData);
      
      // Check role access
      if (requiredRoles.length > 0 && !requiredRoles.includes(userData.role)) {
        // Redirect based on role
        if (userData.role === "admin") {
          navigate("/admin", { replace: true });
        } else if (userData.role === "finance") {
          navigate("/finance", { replace: true });
        } else {
          navigate("/dashboard", { replace: true });
        }
        return;
      }
      
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Auth check error:", error.name, error.message);
      
      // Retry on network errors (cold start, timeout)
      if ((error.name === 'AbortError' || error.name === 'TypeError') && retryCountRef.current < maxRetries) {
        retryCountRef.current += 1;
        const attempt = retryCountRef.current;
        console.log(`Retrying auth check (${attempt}/${maxRetries})...`);
        setLoadingMessage(`Connecting... (attempt ${attempt + 1})`);
        
        // Exponential backoff
        setTimeout(() => checkAuth(), Math.pow(2, attempt - 1) * 1000);
        return;
      }
      
      // Clear token only if it's actually invalid (not just network issue)
      if (error.message === "Not authenticated") {
        localStorage.removeItem("auth_token");
        sessionStorage.removeItem("auth_token_backup");
      }
      
      setIsAuthenticated(false);
      navigate("/login", { replace: true });
    }
  }, [navigate, requiredRoles]);

  useEffect(() => {
    // If user data passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    // Reset retry count on new auth check
    retryCountRef.current = 0;
    setLoadingMessage("Loading...");
    
    checkAuth();
  }, [location.state, checkAuth]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 text-sm">{loadingMessage}</p>
          {loadingMessage.includes("attempt") && (
            <p className="text-slate-400 text-xs">Server may be waking up</p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Clone children and pass user prop
  return children && typeof children.type !== 'string' 
    ? <children.type {...children.props} user={user} />
    : children;
}
